import React, { useCallback, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import "./App.css";
import "../scss/App.scss";
import asyncComponent from "../components/AsyncComponent";
import AnonymousNavbar from "../components/anonymousnavbar";
import NotFound404 from "../components/NotFound404";
import utf8 from "utf8";
import base64 from 'base-64';
import { isIframe, removeQueryStringParams } from "../components/utils";

import { ReactNotifications, Store } from "react-notifications-component";

import * as sessionActions from "../actions/session";
import { getCookie } from "../components/cookies";

import "iframe-resizer/js/iframeResizer.contentWindow.min";
import '../services/apmRum';
import "react-notifications-component/dist/theme.css";
import "react-day-picker/dist/style.css";
import Loading from "../components/Loading";
import MainPage from "./PublicPortal/MainPage";

const Navbar = asyncComponent(() => import("../components/Navbar"));
const AsyncLoginPage = asyncComponent(() => import("../containers/LoginPage"));
const AsyncLandingPage = asyncComponent(() =>
  import("../containers/LandingPage")
);
const AsyncAgentProfile = asyncComponent(() =>
  import("../containers/AgentProfile")
);
const AsyncCorrespondenceMobile = asyncComponent(() =>
  import("../containers/CorrespondenceMobile")
);
const AsyncRecommendedServices = asyncComponent(() =>
  import("../containers/RecommendedServicesMobile")
);
const AsyncDashboardPage = asyncComponent(() =>
  import("../containers/DashboardPage")
);
const AsyncActivityWidget = asyncComponent(() => import("./ActivityWidget"));
const AsyncActivityWidgetV2 = asyncComponent(() => import("./ActivityWidgetV2"));
const AsyncMyListings = asyncComponent(() =>
  import("../containers/MyListingsPage")
);
const AsyncMyListingsFiltered = asyncComponent(() =>
  import("../containers/MyListingsPageFiltered")
);
const AsyncListingDetails = asyncComponent(() =>
  import("../containers/ListingDetailsPage")
);
const AsyncSharedLinkListingsPage = asyncComponent(() =>
  import("../containers/SharedLinkListingsPage")
);
const AsyncCorrespondence = asyncComponent(() =>
  import("../containers/CorrespondencePage")
);
const AsyncSearchPage = asyncComponent(() =>
  import("../containers/SearchPage")
);
const AsyncGoogleAnalytics = asyncComponent(() =>
  import("../components/GoogleAnalytics")
);

const App = (props) => {
  const { session } = props;

  const dispatch = useDispatch();

  const showNotification = useCallback(
    (type, title, message, duration = 8000) => {
      Store.addNotification({
        title,
        message,
        type,
        container: "top-center",
        dismiss: {
          duration,
        },
      });
    },
    []
  );

  const onMessage = useCallback(() => {
    const bc = new BroadcastChannel("LogOut");
    bc.onmessage = (e) => {
      if (e.data === "logout") {
        dispatch({ type: "AUTH_LOGOUT" });
      }
    };
  }, [dispatch]);

  useEffect(() => {
    onMessage();
  }, [onMessage]);

  return (
    <div className="App">
      <Router>
        <div>
          <ReactNotifications />
          <Switch>
            <Route
              path="/login"
              render={(props) => (
                <AsyncLoginPage
                  showNotification={showNotification}
                  {...props}
                />
              )}
            />
            <Route
              path="/shared-link/:permalink/:publicId?"
              exact={true}
              render={(props) =>
                <div>
                  <Navbar
                    dontShowNavItems
                    showNotification={showNotification}
                    {...props}
                  />
                  <AsyncSharedLinkListingsPage
                    publicPortal
                    showNotification={showNotification}
                    {...props}
                  />
                </div>} />

            <Route
              path="/listing-details/public-portal/:listingId?"
              exact={true}
              render={(props) => (
                <div>
                  <Navbar
                    publicPortal
                    showNotification={showNotification}
                    {...props}
                  />
                  <AsyncListingDetails
                    publicPortal
                    showNotification={showNotification}
                    {...props}
                  />
                </div>
              )}
            />

            <Route path="/publicportal" exact={true} component={MainPage} />

            <Route
              path="/search/public/:filters?/:listingID?"
              exact={true}
              render={(props) => (
                <>
                  <Navbar
                    publicPortal
                    showNotification={showNotification}
                    {...props}
                  />
                  <AsyncSearchPage
                    publicPortal
                    showNotification={showNotification}
                    {...props}
                  />
                </>
              )}
            />

            {/* Private Routes that Require Login */}
            <PrivateRoute
              path="/iframe/activity"
              exact={true}
              session={session}
              component={AsyncActivityWidget}
              showNotification={showNotification}
            />

            <PrivateRoute
              path="/iframe/v2/activity"
              exact={true}
              session={session}
              component={() => <AsyncActivityWidgetV2 />}
              showNotification={showNotification}
            />

            <PrivateRoute
              path="/dashboard"
              exact={true}
              session={session}
              component={AsyncDashboardPage}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/agent-profile"
              exact={true}
              session={session}
              component={AsyncAgentProfile}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/correspondance-mobile"
              exact={true}
              session={session}
              component={AsyncCorrespondenceMobile}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/recommended-services"
              exact={true}
              session={session}
              component={AsyncRecommendedServices}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/my-listings"
              exact={true}
              session={session}
              component={AsyncMyListings}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/listing-details/:listingId"
              exact={true}
              session={session}
              component={AsyncListingDetails}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/my-listings-type/:type/:receivedDate?"
              exact={true}
              session={session}
              component={AsyncMyListingsFiltered}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/favorites-and-comments/:type"
              exact={true}
              session={session}
              component={AsyncMyListingsFiltered}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/correspondence/:type"
              exact={true}
              session={session}
              component={AsyncCorrespondence}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/search/:filters?/:listingID?"
              exact={true}
              session={session}
              component={AsyncSearchPage}
              showNotification={showNotification}
            />
            <PrivateRoute
              path="/"
              exact={true}
              session={session}
              component={AsyncLandingPage}
              showNotification={showNotification}
            />
            <Route
              render={(props) => (
                <NotFound404 showNotification={showNotification} {...props} />
              )}
            />
          </Switch>
          <AsyncGoogleAnalytics />
        </div>
      </Router>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    session: state.session,
  };
};

const mapDispatchToProps = (dispatch) => ({
  sessionActions: bindActionCreators(sessionActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);

// Redirect to the component only if the user is logged in, otherwise send to login page
const PrivateRoute = ({
  component: Component,
  session,
  refreshAuth,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      const { isAuthenticating, isAuthenticated } = session;
      // We are not Authenticated and there is no open Authentication requests
      // Redirect to login
      if (isAuthenticated === false && isAuthenticating === false && !getCookie("authDetails")) {
        if (
          props.location &&
          props.location.pathname &&
          props.location.pathname !== "/" &&
          props.location.pathname !== "/profile"
        ) {
          const redirectToUrl = removeQueryStringParams("token");
          window.sessionStorage.setItem(
            "RedirectLocation",
            base64.encode(utf8.encode(redirectToUrl))
          );
        }

        let searchString = props.location.search;
        if (session.expired)
          searchString = searchString
            ? `${searchString}&expired=true`
            : "?expired=true";
        // Push with query string so user will login
        props.history.push(`/login${searchString}`);
        return null;
      }

      // Waiting on Authentication to complete so display a message
      if (isAuthenticating && isAuthenticated === false) {
        return <Loading />;
      }

      // Return the secure page and the navbar
      return (
        <div>
          {!isIframe() &&
            (session.isAuthenticated || getCookie("authDetails")) &&
            rest && !rest.path.includes('/iframe') ? (
            <Navbar showNotification={rest.showNotification} />
          ) : (
            <AnonymousNavbar />
          )}
          <Component {...props} {...rest} />
        </div>
      );
    }}
  />
);
